import { ShoppingListEntry } from "./ShoppingListEntry";

const _ = require("lodash");

interface RelativeIngredientPosition {
  ingredient: string;
  vector: { [key: string]: number };
}

function orderEntriesByBestRoute(
  entries: Array<ShoppingListEntry>,
  fullHistory: Array<ShoppingListEntry>
): Array<ShoppingListEntry> {
  const entryIngredientList = entries.map((x) => x.ingredient.name);
  const relevantHistory = fullHistory.filter(
    (x) => entryIngredientList.includes(x.ingredient.name) && x.shoppedAt
  );

  const distances = extractDistancesFromHistory(relevantHistory);

  const route = bestRouteFromDistances(distances);

  let orderedEntries: Array<ShoppingListEntry> = [];

  route.forEach((position) => {
    orderedEntries = orderedEntries.concat(
      entries.filter((entry) => entry.ingredient.name === position.ingredient)
    );
  });

  const unroutedEntries = entries.filter(
    (entry) => !orderedEntries.includes(entry)
  );
  orderedEntries = orderedEntries.concat(unroutedEntries);

  return orderedEntries;
}

function bestRouteFromDistances(
  distances: Array<RelativeIngredientPosition>
): Array<RelativeIngredientPosition> {
  if (distances.length === 0) {
    return [];
  }

  let unvisitedEntries = _.clone(distances);
  let routedEntries = [];

  let initial = unvisitedEntries.pop();
  routedEntries.push(initial);

  while (unvisitedEntries.length > 0) {
    let current = _.last(routedEntries);

    unvisitedEntries = _.sortBy(unvisitedEntries, (unvisited: any) =>
      distance(unvisited, current)
    );

    routedEntries.push(unvisitedEntries.shift());
  }
  return routedEntries;
}

function distance(
  point1: RelativeIngredientPosition,
  point2: RelativeIngredientPosition
): number {
  let sumOfSquares = 0;

  for (const coordinate in point1.vector) {
    const coordinateDistance =
      point1.vector[coordinate] - point2.vector[coordinate];
    sumOfSquares = +coordinateDistance * coordinateDistance;
  }
  return Math.sqrt(sumOfSquares);
}

function extractDistancesFromHistory(
  history: Array<ShoppingListEntry>
): Array<RelativeIngredientPosition> {
  const ingredientList = _.uniq(history.map((entry) => entry.ingredient.name));

  const distances = ingredientList.map((ingredient: string) => {
    let vector: { [key: string]: number } = {};

    ingredientList.forEach((otherIngredient: string) => {
      vector[otherIngredient] = commonShoppingDistanceBetweenIngredients(
        ingredient,
        otherIngredient,
        history
      );
    });
    return { ingredient, vector };
  });

  return distances;
}

function commonShoppingDistanceBetweenIngredients(
  ingredient: string,
  otherIngredient: string,
  history: Array<ShoppingListEntry>
) {
  if (ingredient === otherIngredient) {
    return 0;
  }

  const ingredientDates = history
    .filter((historyEntry) => historyEntry.ingredient.name === ingredient)
    .map((historyEntry) => historyEntry.shoppedAt);

  const otherIngredientDates = history
    .filter((historyEntry) => historyEntry.ingredient.name === otherIngredient)
    .map((historyEntry) => historyEntry.shoppedAt);

  let allDistances: Array<number> = [];

  ingredientDates.forEach((ingredientDate) => {
    otherIngredientDates.forEach((otherIngredientDate) => {
      allDistances.push(
        Math.abs(ingredientDate.valueOf() - otherIngredientDate.valueOf()) /
          1000
      );
    });
  });

  // 60 minutes max distance, otherwise this must be from a different shop visit
  const relevantDistances = allDistances.filter((x) => x <= 60 * 60);

  if (relevantDistances.length === 0) {
    return 60 * 60;
  }

  const medianDistance = median(relevantDistances);
  return medianDistance;
}

function median(values: Array<number>): number {
  if (values.length === 0) return 0;

  values.sort(function (a, b) {
    return a - b;
  });

  var half = Math.floor(values.length / 2);

  if (values.length % 2) return values[half];

  return (values[half - 1] + values[half]) / 2.0;
}

export {
  RelativeIngredientPosition,
  extractDistancesFromHistory,
  bestRouteFromDistances,
  orderEntriesByBestRoute,
};
